import React, { useState, useContext, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import TeamMemberView from '../components/TeamMemberView';

// Icons
import {
    PersonIcon,
    UserIcon,
    CameraIcon as Camera,
    GlobeIcon,
    SaveIcon,
    SettingsIcon,
    HomeIcon
} from '../components/Icons';
import InlineFieldEditor from '../components/InlineFieldEditor';
import TeamProfileEditor from '../components/TeamProfileEditor';

const MyTeamPage = () => {
    const { profile, refreshProfile } = useOutletContext();
    const { user, API_URL } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Local profile data state for editing
    const [localUserData, setLocalUserData] = useState({});
    const [blocks, setBlocks] = useState([]);
    const [visibilitySettings, setVisibilitySettings] = useState({});
    const [isVisible, setIsVisible] = useState(false);
    const [isFeaturedOnHomepage, setIsFeaturedOnHomepage] = useState(false);
    
    // UI State
    const [editingField, setEditingField] = useState(null);

    // Initial sync
    useEffect(() => {
        if (profile?.userData) {
            setLocalUserData({
                scoutName: profile.userData.scoutName || '',
                job: profile.userData.job || '',
                birthDate: profile.userData.birthDate || '',
                shortDescription: profile.userData.shortDescription || '',
                description: profile.userData.description || '',
                phone: profile.userData.phone || '',
                photos: profile.userData.photos || []
            });
            setBlocks(profile.userData.profileBlocks || []);
            setVisibilitySettings(profile.userData.visibilitySettings || {});
            setIsVisible(profile.userData.isVisible || false);
            setIsFeaturedOnHomepage(profile.userData.isFeaturedOnHomepage || false);
        }
    }, [profile]);

    const handleToggleVisibility = (field, value) => {
        setVisibilitySettings(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async () => {
        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };

            await axios.put(`${API_URL}/users/profile`, {
                userData: {
                    ...profile.userData,
                    ...localUserData,
                    profileBlocks: blocks.map(({ _isNew, ...b }) => b),
                    isVisible,
                    isFeaturedOnHomepage,
                    visibilitySettings
                }
            }, config);

            if (refreshProfile) await refreshProfile();
            setMessage({ type: 'success', text: 'Änderungen erfolgreich gespeichert!' });
            setTimeout(() => setMessage({ type: '', text: '' }), 5000);
        } catch (error) {
            console.error(error);
            setMessage({ type: 'error', text: error.response?.data?.message || 'Fehler beim Speichern.' });
        } finally {
            setLoading(false);
        }
    };

    const handleEditField = (name, value) => {
        const fieldConfigs = {
            scoutName: { label: 'Pfadiname', type: 'text', placeholder: 'Dein Name in der Gruppe' },
            job: { label: 'Aufgabe', type: 'text', placeholder: 'z.B. Gruppenleiter, Materialwart...' },
            shortDescription: { label: 'Kurzbeschreibung', type: 'text', placeholder: 'Ein kurzer Satz über dich', maxLength: 150 },
            description: { label: 'Beschreibung', type: 'textarea', placeholder: 'Erzähle etwas über dich und deine Pfadfinderzeit...' },
            birthDate: { label: 'Geburtsdatum', type: 'date' },
            gallery: { label: 'Fotogalerie', type: 'special' }
        };

        if (name === 'gallery') {
            // Für die Galerie scrollen wir einfach zum Block-Editor oder öffnen einen speziellen Modus
            // In diesem Fall lassen wir TeamProfileEditor das Gallery-Handling machen oder fügen später hinzu.
            setMessage({ type: 'info', text: 'Nutze den Bereich "Optionale Blöcke" unten, um Fotos zu verwalten.' });
            return;
        }

        setEditingField({
            name,
            value: value || '',
            ...fieldConfigs[name]
        });
    };

    const handleSaveInline = (newValue) => {
        setLocalUserData(prev => ({
            ...prev,
            [editingField.name]: newValue
        }));
        setEditingField(null);
    };

    const canManageTeam = profile && (profile.role === 'admin' || profile.role === 'gruppenleiter') && profile.isApproved;

    if (!canManageTeam) {
        return (
            <div className="container mx-auto px-4 py-8 max-w-2xl">
                <div className="bg-error-container text-error-on-container p-6 rounded-2xl flex items-start gap-4">
                    <UserIcon />
                    <div>
                        <h3 className="font-bold text-lg">Kein Zugriff</h3>
                        <p>Deine Seite auf der Website ist nur für aktive Teammitglieder (Gruppenleiter & Admins) verfügbar.</p>
                    </div>
                </div>
            </div>
        );
    }

    // Construct preview data
    const getPreviewData = () => {
        if (!profile) return null;
        
        const name = localUserData.scoutName || profile.userData?.firstName || profile.email;

        return {
            ...profile,
            name: name,
            job: localUserData.job,
            shortDescription: localUserData.shortDescription,
            description: localUserData.description,
            email: profile.email,
            phone: localUserData.phone,
            photos: localUserData.photos || [],
            profileBlocks: blocks || [],
            age: localUserData.birthDate ? Math.floor((new Date() - new Date(localUserData.birthDate)) / 31557600000) : null,
            standardFields: profile.userData?.standardFields,
            userData: localUserData // Pass full local state for consistency
        };
    };

    return (
        <div className="min-h-screen bg-surface-containerLowest">
            {/* Top Toolbar */}
            <div className="sticky top-0 z-30 bg-surface/80 backdrop-blur-xl border-b border-outlineVariant px-6 py-4">
                <div className="max-w-screen-2xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                            <GlobeIcon size={24} />
                        </div>
                        <div>
                            <h1 className="text-title-large font-bold text-surface-on">Webseiten-Sichtbarkeit</h1>
                            <p className="text-label-small text-surface-onVariant uppercase tracking-widest font-bold opacity-60">Konfigurations-Dashboard</p>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        {/* Edit Profile Link */}
                        <Link
                            to="/profile"
                            className="flex items-center gap-2 px-4 py-2 bg-surface-container hover:bg-surface-variant rounded-2xl border border-outlineVariant shadow-sm transition-all text-label-medium font-bold text-surface-onVariant"
                        >
                            <PersonIcon size={18} />
                            <span>Profil bearbeiten</span>
                        </Link>

                        {/* Homepage Toggle */}
                        <div className="flex items-center gap-3 px-4 py-2 bg-surface-container rounded-2xl border border-outlineVariant shadow-sm">
                            <div className="flex items-center gap-2">
                                <HomeIcon size={18} className="text-surface-onVariant opacity-60" />
                                <span className="text-label-medium font-bold text-surface-onVariant">STARTSEITE</span>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={isFeaturedOnHomepage}
                                    onChange={(e) => setIsFeaturedOnHomepage(e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-secondary"></div>
                            </label>
                        </div>

                        {/* Global Visibility Switch */}
                        <div className="flex items-center gap-3 px-4 py-2 bg-surface-container rounded-2xl border border-outlineVariant shadow-sm">
                            <div className="flex items-center gap-2">
                                <GlobeIcon size={18} className="text-surface-onVariant opacity-60" />
                                <span className="text-label-medium font-bold text-surface-onVariant">ÖFFENTLICH</span>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={isVisible}
                                    onChange={(e) => setIsVisible(e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-surface-variant peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                            </label>
                        </div>

                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                            className={`md3-btn-filled rounded-2xl h-11 px-6 flex items-center gap-2 shadow-elevation-2 transition-all ${loading ? 'opacity-70' : ''}`}
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <>
                                    <SaveIcon size={20} />
                                    <span>Speichern</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-screen-2xl mx-auto p-6">
                {message.text && (
                    <div className={`mb-6 p-4 rounded-2xl flex items-center gap-3 animate-scale-in border max-w-xl mx-auto ${message.type === 'success' ? 'bg-success/5 text-success border-success/20' : 'bg-error/5 text-error border-error/20'}`}>
                        <div className="flex-shrink-0">{message.type === 'success' ? '✅' : '❌'}</div>
                        <span className="text-sm font-bold">{message.text}</span>
                    </div>
                )}

                {/* Preview Area */}
                <div className="bg-surface-container rounded-[3rem] border border-outlineVariant shadow-inner overflow-hidden min-h-[800px] relative max-w-screen-xl mx-auto">
                    {/* Background subtle mesh or pattern */}
                    <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[radial-gradient(#4a6741_1px,transparent_1px)] [background-size:20px_20px]" />
                        
                        {/* The Actual Preview */}
                        <div className="relative z-10 p-0 transform scale-[0.95] origin-top transition-all">
                            {editingField && (
                                <div className="absolute top-2 left-1/2 -translate-x-1/2 z-50 w-full max-w-lg">
                                    <InlineFieldEditor 
                                        {...editingField}
                                        onSave={handleSaveInline}
                                        onCancel={() => setEditingField(null)}
                                    />
                                </div>
                            )}
                            <TeamMemberView 
                                member={getPreviewData()} 
                                isEditor={true}
                                visibilitySettings={visibilitySettings}
                                onToggleVisibility={handleToggleVisibility}
                                onEdit={handleEditField}
                            />
                        </div>

                        {!isVisible && (
                            <div className="absolute inset-0 z-20 bg-surface/40 backdrop-blur-[2px] flex items-center justify-center pointer-events-none">
                                <div className="bg-surface-containerHighest border border-outlineVariant p-8 rounded-[2rem] shadow-elevation-4 flex flex-col items-center gap-4 max-w-sm text-center">
                                    <div className="w-16 h-16 rounded-full bg-surface-variant flex items-center justify-center text-surface-onVariant">
                                        <GlobeIcon size={32} />
                                    </div>
                                    <h3 className="text-headline-small font-bold">Nicht Veröffentlicht</h3>
                                    <p className="text-body-medium text-surface-onVariant">Deine Seite ist aktuell für Besucher der Website versteckt.</p>
                                </div>
                            </div>
                        )}
                </div>

                {/* Optional Blocks Editor */}
                <div className="mt-12 max-w-screen-xl mx-auto">
                    <div className="p-8 md:p-12 rounded-[3rem] bg-surface-containerLow border border-outlineVariant shadow-sm">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="w-12 h-12 rounded-2xl bg-secondary/10 text-secondary flex items-center justify-center">
                                <SettingsIcon size={28} />
                            </div>
                            <div>
                                <h1 className="text-display-small font-bold text-surface-on">Optionale Inhalte</h1>
                                <p className="text-body-large text-surface-onVariant">Füge Diagramme, Wortwolken oder Bildergalerien hinzu.</p>
                            </div>
                        </div>

                        <TeamProfileEditor 
                            teamMemberId={profile._id}
                            blocks={blocks}
                            setBlocks={setBlocks}
                            hideSaveButton={true}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MyTeamPage;
